import * as Yup from "yup";

import {
  BusinessPicker,
  InsurerPicker,
  OfficeButton,
  ReInsurerPicker,
  SelectInput,
  Show,
  TableComponent,
  TextInput,
} from "components";
import {
  GenerateFinanceFacultativeReport,
  GenerateFinanceFacultativeReportVariables,
} from "_graphql/mutation/kek/__generated__/GenerateFinanceFacultativeReport";
import {
  facultativeReportTypeOptions,
  insurerTable,
  reinsurerTable,
  wrapClick,
} from "utils";

import { Payment_Type } from "_graphql/__generated__/globalTypes-kek";
import { generateFinanceFacultativeReport } from "_graphql/kek/mutation/index.kek";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";

type Props = {};

const initialValues = {
  client_type: "",
  payment_type: "",
  business_id: "",
  report_type: "",
  insurer_id: "",
  from: "",
  to: "",
};

const validationSchema = Yup.object().shape({
  client_type: Yup.string().required("Client type is required"),
  payment_type: Yup.string().required("Payment type is required"),
  business_id: Yup.string().required("Class of business is required"),
  report_type: Yup.string().required("Report type is required"),
  insurer_id: Yup.string().required("Insurer is required"),
  from: Yup.string().required("Period from is required"),
  to: Yup.string().required("Period to is required"),
});

const Facultative = (props: Props) => {
  const [generate, { data, loading }] = useMutation<
    GenerateFinanceFacultativeReport,
    GenerateFinanceFacultativeReportVariables
  >(generateFinanceFacultativeReport, {
    onCompleted: (data) => {},
    onError: (error) => {
      console.error("Error generating report:", error);
      toast.error("Error generating report");
    },
  });
  console.log(
    "workkdsfsof",
    JSON.parse(data?.generateFinanceFacultativeReport ?? "{}")
  );
  const form = useFormik<any>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      form.setFormikState((state) => ({ ...state, status: "SUBMITTED" }));
      await generate({
        variables: {
          reportData: {
            class_of_business_id: values.business_id,
            client_id: values.insurer_id,
            client_type: values.client_type,
            payment_type: values.payment_type,
            period_from: values.from,
            period_to: values.to,
            report_type: values.report_type,
          },
        },
      });
      form.setSubmitting(false);
      return;
    },
  });

  return (
    <div className="w-full">
      <div className="py-3 flex justify-between">
        <div>
          <h1 className="font-bold text-primary text-lg">Facultative</h1>
        </div>
        <div className="flex items-center space-x-2">
          <OfficeButton
            disabled={loading}
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Generating ..." : "Generate Report"}
          </OfficeButton>
        </div>
      </div>
      <form className="space-y-6 divide-y shadow-sm rounded-md bg-white p-6 divide-gray-200">
        <div>
          <span className="text-xs font-light">Facultative Parameters</span>
          <div className="grid grid-cols-2 gap-5">
            <div className="">
              <SelectInput
                id={"report_type"}
                label={"Report Type"}
                options={facultativeReportTypeOptions}
                {...form}
              />
            </div>
            <div className="">
              <SelectInput
                options={[
                  {
                    label: "--- Select ---",
                    value: "",
                  },
                  {
                    label: "Bank Transfer",
                    value: Payment_Type.BANKTRANSFER,
                  },
                  {
                    label: "Cheque",
                    value: Payment_Type.CHEQUE,
                  },
                ]}
                id="payment_type"
                label="Payment Type"
                placeholder="e.g. Select Payment"
                required={true}
                {...form}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5 my-5">
            <div>
              <BusinessPicker
                addAllOption
                filter={form.values.business_category}
                rawId
                id={"business_id"}
                placeholder="select class of business"
                label={"Class of Business"}
                {...form}
              />
            </div>
            <div>
              <SelectInput
                id={"client_type"}
                label={"Client Type"}
                options={[
                  { label: "Reinsurer", value: "REINSURER" },
                  { label: "Insurer", value: "INSURER" },
                  {
                    label: "Retrocedent",
                    value: "RETROCEDENT",
                  },
                ]}
                {...form}
              />
            </div>
          </div>
          <div className="">
            {form.values.client_type === "INSURER" ? (
              <InsurerPicker
                id="insurer_id"
                label="Insurer"
                placeholder="e.g. Select Insurer"
                required={true}
                disabled={form.values.client_type !== "INSURER"}
                rawId
                {...form}
              />
            ) : (
              <ReInsurerPicker
                id="insurer_id"
                label="Reinsurer"
                placeholder="e.g. Select Reinsurer"
                required={true}
                disabled={
                  form.values.client_type !== "REINSURER" ||
                  form.values.client_type !== "RETROCEDENT"
                }
                rawId
                {...form}
              />
            )}
          </div>
        </div>

        <div className="pt-6">
          <span className="text-xs font-light">Query Date Range</span>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <TextInput
                id="from"
                label={"Period from"}
                type="date"
                placeholder=""
                required={true}
                {...form}
              />
            </div>
            <div>
              <TextInput
                id="to"
                label={"Period to"}
                type="date"
                placeholder=""
                required={true}
                {...form}
              />
            </div>
          </div>
        </div>
      </form>

      <Show if={Boolean(form.status === "SUBMITTED" && form.isValid)}>
        <div className="w-full my-5">
          <TableComponent
            title="Facultative Report"
            loading={loading}
            refetch={() => {}}
            data={JSON.parse(data?.generateFinanceFacultativeReport ?? "[]")}
            columns={
              ["REINSURER", "RETROCEDENT"].includes(form.values.client_type)
                ? reinsurerTable
                : insurerTable
            }
          />
        </div>
      </Show>
    </div>
  );
};

export default Facultative;
